import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useSocket } from '../../../contexts/SocketContext';
import LobbyDetails from './LobbyDetails';
import TeamList from './TeamList';
import ActionButtons from './ActionButtons';
import Preferences from './Preferences';
import ChatBox from './ChatBox';
import Toast from '../../Shared/Toast';
import styles from './LobbyView.module.css';

interface ChatMessage {
  id?: number;
  userId?: number;
  mercenaryId?: number;
  userName?: string;
  mercenaryName?: string;
  message?: string;
  createdAt?: string;
  isSystem?: boolean;
}
interface TeamMember {
  id: number;
  name: string;
  level: number;
  race?: string;
  sex?: string;
  avatar?: number;
  healthPct?: number;
  readyHP?: number;
  surrenderHP?: number;
  strategy?: string;
  isReady?: boolean;
  isBot?: boolean;
}
interface LobbyData {
  id: number;
  name: string;
  minLevel: number;
  maxLevel: number;
  maxPlayers: number;
  randomTeams: boolean;
  pve: boolean;
  matchType: string;
  isRanked: boolean;
  creatorId: number;
  currentParticipants: number;
  teamOne: TeamMember[];
  teamTwo: TeamMember[];
}

const LobbyView: React.FC = () => {
  const { lobbyId } = useParams<{ lobbyId: string }>();
  const [lobby, setLobby] = useState<LobbyData | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInAnyLobby, setIsInAnyLobby] = useState(false);
  const [userLobbyId, setUserLobbyId] = useState<number | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const navigate = useNavigate();
  const { mercenary } = useMercenary();
  const { user } = useAuth();
  const { socket } = useSocket();
  const mercenaryIdRef = useRef<number | undefined>(undefined);
  // lobbyRef always tracks the latest lobby value so socket callbacks
  // (which close over stale state) can safely read isRanked.
  const lobbyRef = useRef<LobbyData | null>(null);
  useEffect(() => { mercenaryIdRef.current = mercenary?.id; }, [mercenary]);
  useEffect(() => { lobbyRef.current = lobby; }, [lobby]);

  const fetchLobby = async () => {
    try {
      const [lobbyRes, partRes, chatRes] = await Promise.all([
        axios.get(`/api/team-battle/lobbies/${lobbyId}`, { withCredentials: true }),
        axios.get('/api/team-battle/participation', { withCredentials: true }),
        axios.get(`/api/team-battle/lobbies/${lobbyId}/chat`, { withCredentials: true }),
      ]);
      setLobby(lobbyRes.data);
      const participation = partRes.data;
      // Use type-specific participation so ranked and regular lobbies are independent
      const isRanked = lobbyRes.data.isRanked;
      const sameType = isRanked
        ? (participation.ranked || { inLobby: false, lobbyId: null })
        : (participation.regular || { inLobby: false, lobbyId: null });
      setIsInAnyLobby(sameType.inLobby);
      setUserLobbyId(sameType.lobbyId ?? null);
      setChatMessages(chatRes.data.map((m: any) => ({
        id: m.id,
        userId: m.userId,
        mercenaryId: m.mercenaryId,
        userName: m.userName,
        mercenaryName: m.mercenaryName,
        message: m.message,
        createdAt: m.createdAt,
      })));
    } catch (err) {
      console.error('Error fetching lobby:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLobby(); }, [lobbyId]);

  // When the active mercenary changes (not on first mount), check if the new
  // merc is in a lobby. If they are in a *different* lobby, navigate there.
  // If they aren't in any lobby, send them back to the lobby list.
  const initialMercIdRef = useRef<number | undefined>(mercenary?.id);
  useEffect(() => {
    if (!mercenary?.id) return;
    // Skip the very first render, the initial fetchLobby already handles it
    if (initialMercIdRef.current === mercenary.id) return;
    initialMercIdRef.current = mercenary.id;

    const checkParticipation = async () => {
      try {
        const { data } = await axios.get('/api/team-battle/participation', { withCredentials: true });
        const currentIsRanked = lobbyRef.current?.isRanked;
        const sameType = currentIsRanked
          ? (data.ranked || { inLobby: false, lobbyId: null })
          : (data.regular || { inLobby: false, lobbyId: null });
        if (sameType.inLobby && String(sameType.lobbyId) !== String(lobbyId)) {
          // New merc is in a different lobby of the same type, navigate there
          navigate(`/team-battle/lobby/${sameType.lobbyId}`, { replace: true });
        } else {
          // New merc is in this lobby, or in no lobby at all, stay on this page
          setIsInAnyLobby(sameType.inLobby);
          setUserLobbyId(sameType.lobbyId ?? null);
        }
      } catch { /* ignore */ }
    };
    checkParticipation();
  }, [mercenary?.id]);

  useEffect(() => {
    if (!socket || !lobbyId) return;
    socket.emit('join_lobby', { lobbyId });

    // On mobile, the socket may disconnect when the browser is backgrounded.
    // Re-join the room and refetch chat history whenever the socket reconnects.
    const onReconnect = () => {
      socket.emit('join_lobby', { lobbyId });
      axios.get(`/api/team-battle/lobbies/${lobbyId}/chat`, { withCredentials: true })
        .then(res => {
          setChatMessages(res.data.map((m: any) => ({
            id: m.id,
            userId: m.userId,
            userName: m.userName,
            mercenaryName: m.mercenaryName,
            mercenaryId: m.mercenaryId,
            message: m.message,
            createdAt: m.createdAt,
          })));
        })
        .catch(() => {});
    };

    socket.on('connect', onReconnect);

    const onMsg = (msg: ChatMessage) => setChatMessages(prev => [...prev, msg]);

    const onUpdate = (data: LobbyData) => {
      setLobby(prev => {
        if (!prev) return data;
        const prevIds = [...(prev.teamOne || []), ...(prev.teamTwo || [])].map(m => m.id);
        const nextIds = [...(data.teamOne || []), ...(data.teamTwo || [])].map(m => m.id);
        const joined = [...(data.teamOne || []), ...(data.teamTwo || [])].filter(m => !prevIds.includes(m.id));
        const left = [...(prev.teamOne || []), ...(prev.teamTwo || [])].filter(m => !nextIds.includes(m.id));
        joined.forEach(m => setChatMessages(msgs => [...msgs, { isSystem: true, message: `${m.name} joined the lobby.` }]));
        left.forEach(m => setChatMessages(msgs => [...msgs, { isSystem: true, message: `${m.name} left the lobby.` }]));
        return data;
      });
      // Update participation state based on whether the current merc is still in the lobby
      const myId = mercenaryIdRef.current;
      if (myId) {
        const allMembers = [...(data.teamOne || []), ...(data.teamTwo || [])];
        const isInLobby = allMembers.some(m => m.id === myId);
        setIsInAnyLobby(isInLobby);
        setUserLobbyId(isInLobby ? data.id : null);
      }
    };

    const onDeleted = () => navigate(lobbyRef.current?.isRanked ? '/ranked-battle' : '/team-battle');

    const onRedirect = ({ battleId, mercenaryId: battleMercId }: { battleId: number; mercenaryId?: number }) => {
      const activeMercenaryId = parseInt(localStorage.getItem('mercenaryId') || '0', 10);
      if (battleMercId && activeMercenaryId && battleMercId !== activeMercenaryId) return;
      navigate(`/battle-result/${battleId}`);
    };

    // When regen fires for the active mercenary, patch their healthPct in the
    // lobby TeamList immediately. The lobby_update from the cron covers all
    // members too, but it can be silently dropped (non-critical try/catch on
    // the server), so this ensures the current user's own bar always updates.
    const onMercenaryRegen = (data: { mercenaryId: number; regen: { hp: number; maxHp?: number } }) => {
      const { mercenaryId: regenMercId, regen } = data;
      const myId = mercenaryIdRef.current;
      if (!myId || regenMercId !== myId) return;
      const maxHp = regen.maxHp ?? 1;
      const healthPct = maxHp > 0
        ? Math.min(100, parseFloat(((regen.hp / maxHp) * 100).toFixed(1)))
        : 100;
      setLobby(prev => {
        if (!prev) return prev;
        const patch = (members: TeamMember[]) =>
          members.map(m => m.id === regenMercId ? { ...m, healthPct } : m);
        return { ...prev, teamOne: patch(prev.teamOne), teamTwo: patch(prev.teamTwo) };
      });
    };

    socket.on('new_message', onMsg);
    socket.on('lobby_update', onUpdate);
    socket.on('lobby_deleted', onDeleted);
    socket.on('redirect_to_battle', onRedirect);
    socket.on('mercenary_regen', onMercenaryRegen);

    return () => {
      socket.off('connect', onReconnect);
      socket.off('new_message', onMsg);
      socket.off('lobby_update', onUpdate);
      socket.off('lobby_deleted', onDeleted);
      socket.off('redirect_to_battle', onRedirect);
      socket.off('mercenary_regen', onMercenaryRegen);
      socket.emit('leave_lobby', { lobbyId });
    };
  }, [socket, lobbyId]);

  const handleKickPlayer = async (targetMercenaryId: number) => {
    if (!mercenary) return;
    try {
      await axios.delete(
        `/api/team-battle/lobbies/${lobbyId}/participants/${targetMercenaryId}`,
        { data: { creatorMercenaryId: mercenary.id }, withCredentials: true }
      );
      setToastType('success');
      setToastMessage('Player kicked.');
    } catch (err: any) {
      setToastType('error');
      setToastMessage(err?.response?.data?.message ?? 'Failed to kick player.');
    }
  };

  const handleBattleStart = async () => {
    if (!lobby) return;
    try {
      const res = await axios.post('/api/battle/team-battle', { lobbyId }, { withCredentials: true });
      navigate(`/battle-result/${res.data.battleId}`);
    } catch (err: any) {
      setToastType('error');
      setToastMessage(err?.response?.data?.message ?? 'Failed to start battle.');
    }
  };

  if (loading) return <div className={styles.loadingState}>Loading lobby room…</div>;
  if (!lobby) return (
    <div className={styles.loadingState}>
      <p>Lobby not found.</p>
      <button
        onClick={() => navigate(-1)}
        style={{ marginTop: '1rem', padding: '0.5rem 1.5rem', cursor: 'pointer' }}
      >
        ← Go Back
      </button>
    </div>
  );

  const isOwner = mercenary?.id === lobby.creatorId;
  const userInCurrentLobby = userLobbyId === lobby.id;
  const allMembers = [...lobby.teamOne, ...lobby.teamTwo];
  // isParticipant: true only when the ACTIVE mercenary is actually in the lobby
  const isParticipant = allMembers.some(m => m.id === mercenary?.id);
  const teamsFull = allMembers.length >= lobby.maxPlayers;

  const myParticipant = allMembers.find(m => m.id === mercenary?.id);
  const defaultPrefs = {
    surrenderHP: myParticipant?.surrenderHP ?? 0.5,
    readyHP: myParticipant?.readyHP ?? 0.9,
    strategy: myParticipant?.strategy ?? 'Normal',
  };

  return (
    <div className={styles.lobbyViewContainer}>
      {toastMessage && (
        <Toast message={toastMessage} type={toastType} duration={3000} onClose={() => setToastMessage(null)} />
      )}

      {/* Row 1 left: Details + Actions */}
      <div className={styles.topArea}>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span className={styles.panelIcon}>⚔</span>
            <span className={styles.panelTitle}>{lobby.name}</span>
          </div>
          <div className={styles.panelBody}>
            <LobbyDetails lobby={lobby} />
            <div className={styles.actionsDivider} />
            <ActionButtons
              isOwner={isOwner}
              userInCurrentLobby={userInCurrentLobby}
              navigate={navigate}
              lobbyId={lobbyId!}
              matchType={lobby.matchType}
              onBattleStart={handleBattleStart}
              teamsFull={teamsFull}
              randomTeams={lobby.randomTeams}
              pve={lobby.pve}
              isRanked={lobby.isRanked}
              setSelectedTeam={() => {}}
              handleKickPlayer={handleKickPlayer}
              isInAnyLobby={isInAnyLobby}
            />
          </div>
        </div>
      </div>

      {/* Row 1 right: Preferences */}
      <div className={styles.sideArea}>
        {isParticipant && mercenary && (
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <span className={styles.panelIcon}>⚙</span>
              <span className={styles.panelTitle}>Preferences</span>
            </div>
            <div className={styles.panelBody}>
              <Preferences
                lobbyId={lobby.id}
                mercenaryId={mercenary.id}
                battleType={lobby.pve ? 'pve' : lobby.matchType}
                preferences={defaultPrefs}
                onUpdate={() => {}}
              />
            </div>
          </div>
        )}
      </div>

      {/* Row 2: Teams - full width */}
      <div className={styles.teamsArea}>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span className={styles.panelIcon}>🛡</span>
            <span className={styles.panelTitle}>Teams</span>
          </div>
          <div className={styles.panelBody}>
            <TeamList
              teamOne={lobby.teamOne}
              teamTwo={lobby.teamTwo}
              ownerMercenaryId={lobby.creatorId}
              isOwner={isOwner}
              handleKickPlayer={handleKickPlayer}
              randomTeams={lobby.randomTeams}
              pve={lobby.pve}
              maxPlayers={lobby.maxPlayers}
              myMercenaryId={mercenary?.id}
            />
          </div>
        </div>
      </div>

      {/* Row 3: Chat - full width */}
      <div className={styles.chatArea}>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span className={styles.panelIcon}>💬</span>
            <span className={styles.panelTitle}>Chat</span>
          </div>
          <div className={styles.panelBody}>
            {user && (
              <ChatBox
                chatMessages={chatMessages}
                lobbyId={lobbyId!}
                socket={socket}
                userId={user.id}
                userName={user.username || user.displayName || ''}
                mercenaryId={mercenary?.id ?? null}
                mercenaryName={mercenary?.name ?? null}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LobbyView;
